import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';
import { API_URL } from '../config';

interface ActivityPhoto { id: number; url: string; }
interface ItineraryStep { time?: string; title?: string; description?: string; }
interface ActivityDetailData {
  id: number;
  name: string;
  description: string;
  price: string;
  inclusions: string[];
  itinerary: ItineraryStep[] | Record<string, string> | null;
  photos: ActivityPhoto[];
}

function normalizeItinerary(raw: ActivityDetailData['itinerary']): ItineraryStep[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return Object.entries(raw).map(([time, description]) => ({ time, description }));
}

const GENERIC_FAQS = [
  { q: 'How do I confirm my booking?', a: "Tap 'Book via WhatsApp' and our team will confirm availability, take your details, and send a payment link — usually within a few hours." },
  { q: 'What is the cancellation policy?', a: 'Cancellation terms depend on the activity and how close to the date you cancel. We\'ll confirm the exact policy for this activity when you book.' },
  { q: 'Can I book for a group?', a: 'Yes — let us know the group size when you message us and we\'ll confirm pricing and availability for everyone together.' },
  { q: 'What should I bring on the day?', a: 'This varies by activity. Once your booking is confirmed, we\'ll send a short checklist so you know exactly what to prepare.' },
];

export default function ActivityDetail() {
  const { id } = useParams<{ id: string }>();
  const [activity, setActivity] = useState<ActivityDetailData | null>(null);
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [activePhoto, setActivePhoto] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [related, setRelated] = useState<ActivityDetailData[]>([]);
  const [relatedStatus, setRelatedStatus] = useState<'loading' | 'success' | 'error'>('loading');

  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
const [showBookingModal, setShowBookingModal] = useState(false);
const [profileLoading, setProfileLoading] = useState(false);
const [bookingStatus, setBookingStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
const [bookingError, setBookingError] = useState('');

const [bookingForm, setBookingForm] = useState({
  firstname: '',
  lastname: '',
  email: '',
  whatsapp_number: '',
  preferred_date: '',
  guests: 1,
});

const updateBookingField = (field: keyof typeof bookingForm, value: string | number) => {
  setBookingForm((prev) => ({ ...prev, [field]: value }));
};

const handleBookNowClick = () => {
  const token = localStorage.getItem('token_customer_silverjarz');

  if (!token) {
    setShowLoginPrompt(true);
    return;
  }

  setProfileLoading(true);
  axios
    .get(`${API_URL}/customer/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => {
      const user = res.data.data;
      const client = user.client || {};
      setBookingForm({
        firstname: client.firstname || '',
        lastname: client.lastname || '',
        email: user.email || client.email || '',
        whatsapp_number: client.phone || '',
        preferred_date: '',
        guests: 1,
      });
      setBookingStatus('idle');
      setBookingError('');
      setShowBookingModal(true);
    })
    .catch(() => {
      // token invalide/expiré -> retombe sur le prompt de login
      localStorage.removeItem('token_customer_silverjarz');
      setShowLoginPrompt(true);
    })
    .finally(() => setProfileLoading(false));
};

const handleBookingSubmit = () => {
  if (!activity) return;
  const token = localStorage.getItem('token_customer_silverjarz');
  if (!token) {
    setShowBookingModal(false);
    setShowLoginPrompt(true);
    return;
  }

  if (!bookingForm.preferred_date || !bookingForm.whatsapp_number || !bookingForm.firstname || !bookingForm.lastname || !bookingForm.email) {
    setBookingError('Merci de remplir tous les champs.');
    return;
  }

  setBookingStatus('submitting');
  setBookingError('');

  axios
    .post(
      `${API_URL}/activity-bookings`,
      { activity_id: activity.id, ...bookingForm },
      { headers: { Authorization: `Bearer ${token}` } }
    )
    .then(() => {
      setBookingStatus('success');
    })
    .catch((err) => {
      setBookingStatus('error');
      setBookingError(err.response?.data?.message || "Une erreur s'est produite, réessayez.");
    });
};

  useEffect(() => {
    setStatus('loading');
    setActivePhoto(0);
    window.scrollTo({ top: 0 });
    axios.get(`${API_URL}/activities-list/${id}`)
      .then((res) => { setActivity(res.data.data); setStatus('success'); })
      .catch(() => setStatus('error'));
  }, [id]);

  useEffect(() => {
    setRelatedStatus('loading');
    axios.get(`${API_URL}/activities-list`)
      .then((res) => {
        const all: ActivityDetailData[] = res.data.data || [];
        setRelated(all.filter((a) => String(a.id) !== String(id)).slice(0, 3));
        setRelatedStatus('success');
      })
      .catch(() => setRelatedStatus('error'));
  }, [id]);

  const whatsappHref = activity
    ? `https://wa.me/96893579571?text=${encodeURIComponent(`Hi, I'd like to book: ${activity.name}`)}`
    : '#';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        .ad-page {
          --teal: #294b5a; --teal-d: #1a3140; --gold: #d3be76; --gold-d: #b09a50;
          --cream: #f7f4ee; --mist: #edeae2; --silver: #8a9aa3; --ink: #1a2f38;
          --radius: 12px;
          font-family: 'DM Sans', sans-serif;
          background: var(--cream);
        }
        .ad-page h1, .ad-page h2, .ad-page h3 { font-family: 'Playfair Display', serif; }
        .ad-container { max-width: 1180px; margin: 0 auto; padding: 0 24px; }
        @media (max-width: 576px) { .ad-container { padding: 0 16px; } }

        /* ================= HERO ================= */
        .ad-hero {
          position: relative;
          margin-top: 84px;
          height: 62vh;
          min-height: 440px;
          max-height: 620px;
          overflow: hidden;
          background: var(--teal-d);
          border-radius: 0 0 28px 28px;
        }
        @media (max-width: 700px) { .ad-hero { margin-top: 76px; min-height: 380px; } }
        .ad-hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
        .ad-hero-veil {
          position: absolute; inset: 0;
          background: linear-gradient(0deg, rgba(14,25,30,.92) 0%, rgba(14,25,30,.35) 45%, rgba(14,25,30,.08) 70%);
        }
        .ad-hero-body { position: absolute; left: 0; right: 0; bottom: 0; z-index: 2; padding: 0 0 96px; }
        .ad-breadcrumb { font-size: 11.5px; color: rgba(255,255,255,.55); margin-bottom: 16px; }
        .ad-breadcrumb a { color: rgba(255,255,255,.8); text-decoration: none; }
        .ad-breadcrumb a:hover { color: var(--gold); }
        .ad-hero-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 700; letter-spacing: 1.5px; color: var(--gold); margin-bottom: 14px; }
        .ad-hero-eyebrow::before { content: ''; width: 20px; height: 2px; background: var(--gold); border-radius: 2px; }
        .ad-hero-title { font-size: clamp(30px, 4vw, 48px); font-weight: 600; color: #fff; line-height: 1.15; max-width: 700px; margin-bottom: 14px; text-shadow: 0 2px 20px rgba(0,0,0,.3); }
        .ad-hero-tagline { font-size: 15px; color: rgba(255,255,255,.8); font-weight: 300; max-width: 520px; line-height: 1.65; }

        /* ================= FLOATING TICKET CARD ================= */
        .ad-ticket-wrap { position: relative; z-index: 3; margin-top: -64px; padding-bottom: 8px; }
        .ad-ticket {
          background: #fff;
          border-radius: var(--radius);
          box-shadow: 0 28px 64px rgba(26,49,61,.22);
          display: grid;
          grid-template-columns: 1fr auto 240px;
          align-items: center;
        }
        @media (max-width: 800px) { .ad-ticket { grid-template-columns: 1fr; } }

        .ad-ticket-thumbs { display: flex; gap: 8px; padding: 18px 22px; overflow-x: auto; }
        .ad-thumb { width: 76px; height: 54px; border-radius: 8px; overflow: hidden; flex-shrink: 0; cursor: pointer; border: 2px solid transparent; opacity: .55; transition: opacity .2s, border-color .2s; }
        .ad-thumb.active { opacity: 1; border-color: var(--gold); }
        .ad-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }

        .ad-ticket-perforation { position: relative; width: 0; height: 70%; border-left: 2px dashed var(--mist); }
        .ad-ticket-perforation::before, .ad-ticket-perforation::after { content: ''; position: absolute; left: -11px; width: 22px; height: 22px; border-radius: 50%; background: var(--cream); }
        .ad-ticket-perforation::before { top: -11px; }
        .ad-ticket-perforation::after { bottom: -11px; }
        @media (max-width: 800px) { .ad-ticket-perforation { display: none; } }

        .ad-ticket-cta { padding: 20px 24px; text-align: right; }
        @media (max-width: 800px) { .ad-ticket-cta { text-align: left; border-top: 1px dashed var(--mist); } }
        .ad-ticket-price-label { font-size: 10px; font-weight: 700; letter-spacing: .6px;  color: var(--silver); }
        .ad-ticket-price { font-family: 'Playfair Display', serif; font-size: 1.7rem; font-weight: 600; color: var(--ink); margin: 2px 0 12px; }
        .ad-ticket-price sup { font-size: .5em; font-weight: 500; color: var(--silver); }
        .ad-whatsapp-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 9px;
          background: #25D366; color: #fff; border: none; border-radius: 7px;
          padding: 13px 20px; font-size: 13px; font-weight: 700;
          text-decoration: none; transition: background .2s, transform .2s; width: 100%;
        }
        .ad-whatsapp-btn:hover { background: #1ebe5a; transform: translateY(-1px); color: #fff; }

        /* ================= BODY ================= */
        .ad-body-grid { display: grid; grid-template-columns: 1fr 300px; gap: 56px; padding: 56px 0 20px; align-items: start; }
        @media (max-width: 991px) { .ad-body-grid { grid-template-columns: 1fr; gap: 40px; } }

        /* ---- Info cards (Overview / Inclusions / Itinerary / FAQ) ---- */
        .ad-info-card {
          background: #fff;
          border-radius: var(--radius);
          border: 1px solid rgba(41,75,90,.08);
          box-shadow: 0 6px 20px rgba(26,49,61,.06);
          padding: 34px 36px;
          margin-bottom: 24px;
        }
        .ad-info-card:last-child { margin-bottom: 0; }
        @media (max-width: 576px) { .ad-info-card { padding: 26px 22px; } }

        .ad-section-label { display: inline-flex; align-items: center; gap: 8px; font-size: 10.5px; font-weight: 700; letter-spacing: 1.2px; color: var(--gold-d); margin-bottom: 16px; }
        .ad-section-label::before { content: ''; width: 18px; height: 2px; background: var(--gold); border-radius: 2px; }
        .ad-section-title { font-size: 1.3rem; font-weight: 600; color: var(--ink); margin-bottom: 20px; }

        .ad-desc { font-size: 15.5px; line-height: 1.9; color: #4d5c63; margin: 0; white-space: pre-line; }

        .ad-inclusion-list { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 24px; }
        @media (max-width: 500px) { .ad-inclusion-list { grid-template-columns: 1fr; } }
        .ad-inclusion-item { display: flex; align-items: center; gap: 12px; font-size: 14px; color: var(--ink); font-weight: 500; }
        .ad-inclusion-check { width: 26px; height: 26px; border-radius: 50%; background: rgba(211,190,118,.15); color: var(--gold-d); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

        .ad-itinerary { position: relative; padding-left: 6px; }
        .ad-step { position: relative; display: flex; gap: 20px; padding-bottom: 32px; }
        .ad-step:last-child { padding-bottom: 0; }
        .ad-step-rail { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }
        .ad-step-ring { width: 40px; height: 40px; border-radius: 50%; border: 1px solid var(--gold); background: #fff; display: flex; align-items: center; justify-content: center; font-family: 'Playfair Display', serif; font-weight: 600; font-size: 13px; color: var(--teal); box-shadow: 0 0 0 5px var(--cream); }
        .ad-step-line { width: 1px; flex: 1; background: rgba(211,190,118,.4); margin-top: 6px; }
        .ad-step-time { font-size: 11px; font-weight: 700; letter-spacing: .4px; color: var(--gold-d);  margin-bottom: 3px; }
        .ad-step-title { font-size: 15.5px; font-weight: 600; color: var(--ink); margin-bottom: 5px; }
        .ad-step-desc { font-size: 13.5px; color: var(--silver); line-height: 1.7; }

        .ad-faq-item { border-bottom: 1px solid rgba(41,75,90,.1); }
        .ad-faq-item:first-child { border-top: 1px solid rgba(41,75,90,.1); }
        .ad-faq-q { width: 100%; background: none; border: none; text-align: left; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 20px 2px; font-family: 'Playfair Display', serif; font-size: 1.02rem; font-weight: 500; color: var(--ink); }
        .ad-faq-icon { flex: none; width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid var(--gold-d); position: relative; transition: transform .3s, background .3s; }
        .ad-faq-icon::before, .ad-faq-icon::after { content: ''; position: absolute; background: var(--gold-d); top: 50%; left: 50%; transform: translate(-50%,-50%); }
        .ad-faq-icon::before { width: 9px; height: 1.4px; }
        .ad-faq-icon::after { width: 1.4px; height: 9px; transition: opacity .2s; }
        .ad-faq-item.open .ad-faq-icon { background: var(--gold-d); transform: rotate(180deg); }
        .ad-faq-item.open .ad-faq-icon::before, .ad-faq-item.open .ad-faq-icon::after { background: #fff; }
        .ad-faq-item.open .ad-faq-icon::after { opacity: 0; }
        .ad-faq-a { max-height: 0; overflow: hidden; transition: max-height .35s ease; }
        .ad-faq-item.open .ad-faq-a { max-height: 220px; }
        .ad-faq-a p { font-size: 13.5px; color: var(--silver); font-weight: 300; line-height: 1.75; padding: 0 2px 24px; max-width: 600px; margin: 0; }

        /* ---- Sidebar ---- */
        .ad-sidebar { position: sticky; top: 110px; display: flex; flex-direction: column; gap: 16px; }
        .ad-help-card { background: var(--teal-d); border-radius: var(--radius); padding: 26px 24px; color: #fff; }
        .ad-help-card h4 { font-size: 15px; font-weight: 700; margin-bottom: 8px; }
        .ad-help-card p { font-size: 13px; color: rgba(255,255,255,.65); line-height: 1.7; margin-bottom: 14px; }
        .ad-help-card a { display: inline-flex; align-items: center; gap: 6px; color: var(--gold); font-weight: 600; font-size: 13px; text-decoration: none; }

        .ad-trust-item { display: flex; align-items: flex-start; gap: 12px; padding: 14px 0; border-bottom: 1px solid rgba(41,75,90,.08); }
        .ad-trust-item:last-child { border-bottom: none; }
        .ad-trust-icon { width: 30px; height: 30px; border-radius: 50%; background: rgba(211,190,118,.15); color: var(--gold-d); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .ad-trust-text { font-size: 12.5px; color: var(--ink); font-weight: 500; line-height: 1.5; }

        /* ---- Related ---- */
        .ad-related-section { padding: 20px 0 90px; }
        .ad-related-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; margin-top: 30px; }
        @media (max-width: 900px) { .ad-related-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 600px) { .ad-related-grid { grid-template-columns: 1fr; } }
        .ad-related-card { position: relative; display: block; border-radius: var(--radius); overflow: hidden; aspect-ratio: 3/4; text-decoration: none; box-shadow: 0 6px 20px rgba(26,49,61,.08); transition: transform .35s cubic-bezier(.22,.68,0,1.1), box-shadow .35s; }
        .ad-related-card:hover { transform: translateY(-6px); box-shadow: 0 20px 40px rgba(26,49,61,.16); }
        .ad-related-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: transform .6s ease; }
        .ad-related-card:hover .ad-related-img { transform: scale(1.08); }
        .ad-related-overlay { position: absolute; inset: 0; background: linear-gradient(0deg, rgba(15,25,30,.92) 0%, rgba(15,25,30,.3) 50%, transparent 75%); }
        .ad-related-price { position: absolute; top: 14px; right: 14px; background: rgba(255,255,255,.92); color: var(--ink); font-size: 11.5px; font-weight: 700; padding: 5px 11px; border-radius: 999px; }
        .ad-related-body { position: absolute; left: 0; right: 0; bottom: 0; padding: 18px; }
        .ad-related-name { font-family: 'Playfair Display', serif; font-size: 1.08rem; font-weight: 600; color: #fff; margin-bottom: 8px; line-height: 1.3; }
        .ad-related-arrow { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; color: var(--gold); }

        /* ---- Skeletons ---- */
        @keyframes ad-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        .ad-skel { background: linear-gradient(90deg, #dfe0d6 25%, #cfd0c4 50%, #dfe0d6 75%); background-size: 200% 100%; animation: ad-shimmer 1.5s infinite; }
        @media (prefers-reduced-motion: reduce) { .ad-skel { animation: none; } }

        .ad-state { text-align: center; padding: 100px 20px; color: var(--silver); }
        .ad-state a { color: var(--gold-d); font-weight: 600; }

        .ad-booknow-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  background: var(--teal); color: #fff; border: none; border-radius: 7px;
  padding: 13px 20px; font-size: 13px; font-weight: 700;
  width: 100%; margin-bottom: 10px; cursor: pointer;
  transition: background .2s, transform .2s;
}
.ad-booknow-btn:hover { background: var(--teal-d); transform: translateY(-1px); }
.ad-booknow-btn:disabled { opacity: .6; cursor: not-allowed; transform: none; }

/* ===== Modal overlay shared ===== */
@keyframes ad-modal-in {
  from { opacity: 0; transform: translateY(14px) scale(.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes ad-overlay-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.ad-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(15,25,30,.6);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 1000; padding: 20px;
  animation: ad-overlay-in .25s ease;
}
.ad-modal {
  background: #fff;
  border-radius: 16px;
  max-width: 460px; width: 100%;
  position: relative;
  box-shadow: 0 40px 90px rgba(15,25,30,.35);
  animation: ad-modal-in .32s cubic-bezier(.22,.68,0,1.05);
  overflow: hidden;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.ad-modal-topbar {
  height: 5px;
  background: linear-gradient(90deg, var(--gold-d), var(--gold), var(--gold-d));
  flex-shrink: 0;
}
.ad-modal-scroll { padding: 38px 38px 34px; overflow-y: auto; }
@media (max-width: 480px) { .ad-modal-scroll { padding: 30px 24px 26px; } }

.ad-modal-close {
  position: absolute; top: 20px; right: 20px; width: 32px; height: 32px;
  border-radius: 50%; border: 1px solid rgba(41,75,90,.15); background: #fff;
  cursor: pointer; z-index: 2;
  display: flex; align-items: center; justify-content: center; color: var(--ink);
  transition: background .2s, transform .2s;
}
.ad-modal-close:hover { background: var(--mist); transform: rotate(90deg); }

.ad-modal-icon {
  width: 52px; height: 52px; border-radius: 50%;
  background: rgba(211,190,118,.14);
  display: flex; align-items: center; justify-content: center;
  color: var(--gold-d); margin-bottom: 20px;
}

.ad-modal-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 10.5px; font-weight: 700; letter-spacing: 1.4px;
  color: var(--gold-d); margin-bottom: 12px;
}
.ad-modal-eyebrow::before { content: ''; width: 18px; height: 2px; background: var(--gold); border-radius: 2px; }

.ad-modal h3 {
  font-size: 1.5rem; font-weight: 600; color: var(--ink);
  margin-bottom: 12px; line-height: 1.3;
}
.ad-modal p.ad-modal-sub {
  font-size: 14px; color: var(--silver); line-height: 1.75; margin-bottom: 26px;
}
.ad-modal-divider { height: 1px; background: rgba(41,75,90,.1); margin: 0 0 26px; }

.ad-modal-login-cta {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  background: var(--teal); color: #fff; text-decoration: none; border-radius: 8px;
  padding: 15px 20px; font-size: 13.5px; font-weight: 700; width: 100%;
  transition: background .2s, transform .2s;
}
.ad-modal-login-cta:hover { background: var(--teal-d); color: #fff; transform: translateY(-1px); }

.ad-modal-secondary-note {
  text-align: center; font-size: 12px; color: var(--silver); margin-top: 16px;
}

/* ===== Booking form ===== */
.ad-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 480px) { .ad-form-row { grid-template-columns: 1fr; } }

.ad-form-group { margin-bottom: 18px; }
.ad-form-label {
  display: block; font-size: 10.5px; font-weight: 700; letter-spacing: .6px;
  color: var(--silver); margin-bottom: 8px;
}

.ad-form-input {
  width: 100%;
  height: 48px;
  padding: 0 15px;
  border-radius: 9px;
  border: 1px solid rgba(41,75,90,.14) !important;
  font-size: 14.5px;
  font-family: 'DM Sans', sans-serif;
  color: var(--ink);
  background: var(--cream);
  transition: border-color .2s, box-shadow .2s, background .2s;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
}
.ad-form-input::placeholder { color: var(--silver); opacity: .75; }
.ad-form-input:hover { border-color: rgba(41,75,90,.28); }
.ad-form-input:focus {
  outline: none;
  background: #fff;
  border-color: var(--gold);
  box-shadow: 0 0 0 4px rgba(211,190,118,.16);
}

input[type="date"].ad-form-input { color-scheme: light; }
input[type="date"].ad-form-input::-webkit-calendar-picker-indicator {
  filter: invert(46%) sepia(11%) saturate(487%) hue-rotate(153deg) brightness(90%);
  cursor: pointer;
  opacity: .75;
}

.ad-stepper-row {
  display: flex; align-items: center; justify-content: space-between;
  background: var(--cream); border: 1px solid rgba(41,75,90,.14);
  border-radius: 9px; padding: 0 12px 0 15px;
  height: 48px;
  box-sizing: border-box;
  transition: border-color .2s;
}
.ad-stepper-row:hover { border-color: rgba(41,75,90,.28); }
.ad-stepper-row .ad-stepper-label { font-size: 14px; color: var(--ink); font-weight: 600; }
.ad-stepper { display: flex; align-items: center; gap: 10px; }
.ad-stepper-btn {
  width: 28px; height: 28px; border-radius: 50%; border: 1px solid var(--gold);
  background: #fff; color: var(--teal); font-size: 15px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  flex-shrink: 0;
  transition: background .2s, color .2s, border-color .2s;
}
.ad-stepper-btn:hover:not(:disabled) { background: var(--gold); border-color: var(--gold); color: #fff; }
.ad-stepper-btn:disabled { opacity: .3; cursor: not-allowed; }

.ad-form-error {
  font-size: 12.5px; color: #a5433f; background: rgba(165,67,63,.08);
  border: 1px solid rgba(165,67,63,.2); border-radius: 7px;
  padding: 10px 14px; margin-bottom: 18px;
}

.ad-form-submit-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 9px;
  background: var(--teal); color: #fff; border: none; border-radius: 9px;
  padding: 15px 20px; font-size: 13.5px; font-weight: 700;
  width: 100%; cursor: pointer; margin-top: 4px;
  transition: background .2s, transform .2s;
}
.ad-form-submit-btn:hover:not(:disabled) { background: var(--teal-d); transform: translateY(-1px); }
.ad-form-submit-btn:disabled { opacity: .6; cursor: not-allowed; transform: none; }

.ad-form-success {
  text-align: center;
  padding: 12px 4px 6px;
}
.ad-form-success-icon {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: rgba(37,211,102,.12);
  border: 1px solid rgba(37,211,102,.25);
  color: #1ea952;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 22px;
}
.ad-form-success-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 10.5px; font-weight: 700; letter-spacing: 1.4px;
  text-transform: uppercase; color: var(--gold-d); margin-bottom: 10px;
}
.ad-form-success h4 {
  font-size: 1.3rem;
  font-family: 'Playfair Display', serif;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 12px;
}
.ad-form-success p {
  font-size: 14px;
  color: var(--silver);
  line-height: 1.75;
  max-width: 320px;
  margin: 0 auto 24px;
}
.ad-form-success-divider {
  height: 1px;
  background: rgba(41,75,90,.1);
  margin: 0 0 22px;
}
.ad-form-success-note {
  font-size: 12.5px;
  color: var(--silver);
  background: var(--cream);
  border-radius: 8px;
  padding: 12px 16px;
  text-align: left;
}
.ad-form-success-close-btn {
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--teal); color: #fff; border: none; border-radius: 9px;
  padding: 14px 20px; font-size: 13.5px; font-weight: 700;
  width: 100%; cursor: pointer; margin-top: 20px;
  transition: background .2s, transform .2s;
}
.ad-form-success-close-btn:hover { background: var(--teal-d); transform: translateY(-1px); }

      `}</style>

      <div className="ad-page">
        <Header />

        {status === 'loading' && (
          <>
            <div className="ad-hero"><div className="ad-skel" style={{ position: 'absolute', inset: 0 }} /></div>
            <div className="ad-ticket-wrap"><div className="ad-container"><div className="ad-skel" style={{ height: 96, borderRadius: 12 }} /></div></div>
            <div className="ad-container">
              <div className="ad-body-grid">
                <div>
                  <div className="ad-info-card">
                    <div className="ad-skel" style={{ height: 14, width: '95%', marginBottom: 10, borderRadius: 6 }} />
                    <div className="ad-skel" style={{ height: 14, width: '88%', marginBottom: 10, borderRadius: 6 }} />
                    <div className="ad-skel" style={{ height: 14, width: '60%', borderRadius: 6 }} />
                  </div>
                </div>
                <div className="ad-skel" style={{ height: 220, borderRadius: 12 }} />
              </div>
            </div>
          </>
        )}

        {status === 'error' && (
          <div className="ad-state" style={{ marginTop: 130 }}>
            We couldn't find this activity. It may have been removed.<br />
            <Link to="/services">Browse all activities →</Link>
          </div>
        )}

        {status === 'success' && activity && (
          <>
            <section className="ad-hero">
              <img className="ad-hero-img" src={activity.photos?.[activePhoto]?.url} alt={activity.name} />
              <div className="ad-hero-veil" />
              <div className="ad-container ad-hero-body">
                <div className="ad-breadcrumb">
                  <Link to="/">Home</Link> / <Link to="/services">Activities</Link> / {activity.name}
                </div>
                <div className="ad-hero-eyebrow">Silverjarz Experience</div>
                <h1 className="ad-hero-title">{activity.name}</h1>
                <p className="ad-hero-tagline">{activity.description.split('.')[0]}.</p>
              </div>
            </section>

            <div className="ad-ticket-wrap">
              <div className="ad-container">
                <div className="ad-ticket">
                  {activity.photos && activity.photos.length > 1 ? (
                    <div className="ad-ticket-thumbs">
                      {activity.photos.map((p, i) => (
                        <div key={p.id} className={`ad-thumb${i === activePhoto ? ' active' : ''}`} onClick={() => setActivePhoto(i)}>
                          <img src={p.url} alt="" />
                        </div>
                      ))}
                    </div>
                  ) : <div />}
                  <div className="ad-ticket-perforation" />

                 <div className="ad-ticket-cta">
  <div className="ad-ticket-price-label">Price per person</div>
  <div className="ad-ticket-price">{activity.price}<sup> OMR</sup></div>

  <button
    type="button"
    className="ad-booknow-btn"
    onClick={handleBookNowClick}
    disabled={profileLoading}
  >
    {profileLoading ? 'Loading…' : 'Book Now'}
  </button>

  <a href={whatsappHref} target="_blank" rel="noreferrer" className="ad-whatsapp-btn">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2z" /></svg>
    Book via WhatsApp
  </a>
</div>

                </div>
              </div>
            </div>

            <div className="ad-container">
              <div className="ad-body-grid">
                <div>

                  {/* ---- Overview card ---- */}
                  <div className="ad-info-card">
                    <span className="ad-section-label">Overview</span>
                    <p className="ad-desc">{activity.description}</p>
                  </div>

                  {/* ---- What's Included card ---- */}
                  {activity.inclusions && activity.inclusions.length > 0 && (
                    <div className="ad-info-card">
                      <span className="ad-section-label">What's Included</span>
                      <div className="ad-inclusion-list">
                        {activity.inclusions.map((inc, i) => (
                          <div className="ad-inclusion-item" key={i}>
                            <span className="ad-inclusion-check">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                            </span>
                            {inc}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ---- Itinerary card ---- */}
                  {normalizeItinerary(activity.itinerary).length > 0 && (
                    <div className="ad-info-card">
                      <span className="ad-section-label">Itinerary</span>
                      <div className="ad-itinerary">
                        {normalizeItinerary(activity.itinerary).map((step, i, arr) => (
                          <div className="ad-step" key={i}>
                            <div className="ad-step-rail">
                              <div className="ad-step-ring">{i + 1}</div>
                              {i < arr.length - 1 && <div className="ad-step-line" />}
                            </div>
                            <div>
                              {step.time && <div className="ad-step-time">{step.time}</div>}
                              {step.title && <div className="ad-step-title">{step.title}</div>}
                              {step.description && <div className="ad-step-desc">{step.description}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ---- FAQ card ---- */}
                  <div className="ad-info-card">
                    <span className="ad-section-label">Good to Know</span>
                    <h2 className="ad-section-title">Frequently Asked Questions</h2>
                    <div>
                      {GENERIC_FAQS.map((f, idx) => {
                        const isOpen = openFaq === idx;
                        return (
                          <div className={`ad-faq-item${isOpen ? ' open' : ''}`} key={f.q}>
                            <button type="button" className="ad-faq-q" onClick={() => setOpenFaq(isOpen ? null : idx)} aria-expanded={isOpen}>
                              <span>{f.q}</span>
                              <span className="ad-faq-icon" />
                            </button>
                            <div className="ad-faq-a"><p>{f.a}</p></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>

                <div className="ad-sidebar">
                  <div className="ad-help-card">
                    <h4>Need Help Choosing?</h4>
                    <p>Our team is available 24/7 to help you plan the perfect Oman experience.</p>
                    <Link to="/contact">
                      Contact Us
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                    </Link>
                  </div>

                  <div style={{ background: '#fff', borderRadius: 12, padding: '20px 22px', border: '1px solid rgba(41,75,90,.08)' }}>
                    <div className="ad-trust-item">
                      <span className="ad-trust-icon"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg></span>
                      <span className="ad-trust-text">Secure booking, no upfront payment required</span>
                    </div>
                    <div className="ad-trust-item">
                      <span className="ad-trust-icon"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg></span>
                      <span className="ad-trust-text">Confirmation within a few hours</span>
                    </div>
                    <div className="ad-trust-item">
                      <span className="ad-trust-icon"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3" /></svg></span>
                      <span className="ad-trust-text">Local Omani team, available every day</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {(relatedStatus === 'loading' || (relatedStatus === 'success' && related.length > 0)) && (
              <div className="ad-container">
                <div className="ad-related-section">
                  <span className="ad-section-label">Keep Exploring</span>
                  <h2 className="ad-section-title">You Might Also Like</h2>
                  <div className="ad-related-grid">
                    {relatedStatus === 'loading' && [0, 1, 2].map((i) => (
                      <div key={i} className="ad-skel" style={{ aspectRatio: '3/4', borderRadius: 12 }} />
                    ))}
                    {relatedStatus === 'success' && related.map((r) => (
                      <Link to={`/activities/${r.id}`} className="ad-related-card" key={r.id}>
                        <img className="ad-related-img" src={r.photos?.[0]?.url} alt={r.name} />
                        <div className="ad-related-overlay" />
                        <span className="ad-related-price">{r.price} OMR</span>
                        <div className="ad-related-body">
                          <div className="ad-related-name">{r.name}</div>
                          <span className="ad-related-arrow">
                            View activity
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

{showLoginPrompt && (
  <div className="ad-modal-overlay" onClick={() => setShowLoginPrompt(false)}>
    <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
      <div className="ad-modal-topbar" />
      <div className="ad-modal-scroll">
        <button className="ad-modal-close" onClick={() => setShowLoginPrompt(false)} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="ad-modal-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M12 2l2.9 6.3L22 9.3l-5 4.9 1.2 7-6.2-3.4-6.2 3.4L7 14.2 2 9.3l7.1-1z" />
          </svg>
        </div>

        <span className="ad-modal-eyebrow">Silverjarz Experience</span>
        <h3>Live an unforgettable journey</h3>
        <p className="ad-modal-sub">
          Sign in to your Silverjarz account to reserve "{activity?.name}" in just a few seconds
          and track your booking anytime, anywhere.
        </p>
        <div className="ad-modal-divider" />

        <Link to="/login" className="ad-modal-login-cta">
          Sign in to book
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
          </svg>
        </Link>
        <div className="ad-modal-secondary-note">Don't have an account yet? You can create one in seconds.</div>
      </div>
    </div>
  </div>
)}

{showBookingModal && activity && (
  <div className="ad-modal-overlay" onClick={() => setShowBookingModal(false)}>
    <div className="ad-modal" onClick={(e) => e.stopPropagation()}>
      <div className="ad-modal-topbar" />
      <div className="ad-modal-scroll">
        <button className="ad-modal-close" onClick={() => setShowBookingModal(false)} aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {bookingStatus === 'success' ? (
  <div className="ad-form-success">
    <div
      className="ad-form-success-icon"
      style={{
        width: 68,
        height: 68,
        borderRadius: '50%',
        background: 'rgba(37,211,102,.12)',
        border: '1px solid rgba(37,211,102,.25)',
        color: '#1ea952',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 22px',
      }}
    >
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>

    <span className="ad-form-success-eyebrow">Request Confirmed</span>
    <h4>Booking request sent</h4>
    <p>
      Our team will confirm your reservation for "{activity.name}" shortly and reach out on WhatsApp
      at {bookingForm.whatsapp_number || 'your number'}.
    </p>

    <div className="ad-form-success-divider" />
    <div className="ad-form-success-note">
      A confirmation email has also been sent to <strong>{bookingForm.email}</strong>.
    </div>

    <button
      type="button"
      className="ad-form-success-close-btn"
      onClick={() => setShowBookingModal(false)}
    >
      Done
    </button>
  </div>
) : (
          <>
            <span className="ad-modal-eyebrow">Reserve Your Spot</span>
            <h3>{activity.name}</h3>
            <p className="ad-modal-sub">Confirm your details below and pick your preferred date.</p>

            {bookingError && <div className="ad-form-error">{bookingError}</div>}

            <div className="ad-form-row">
              <div className="ad-form-group">
                <label className="ad-form-label">First Name</label>
                <input
                  className="ad-form-input"
                  value={bookingForm.firstname}
                  onChange={(e) => updateBookingField('firstname', e.target.value)}
                />
              </div>
              <div className="ad-form-group">
                <label className="ad-form-label">Last Name</label>
                <input
                  className="ad-form-input"
                  value={bookingForm.lastname}
                  onChange={(e) => updateBookingField('lastname', e.target.value)}
                />
              </div>
            </div>

            <div className="ad-form-group">
              <label className="ad-form-label">Email</label>
              <input
                type="email"
                className="ad-form-input"
                value={bookingForm.email}
                onChange={(e) => updateBookingField('email', e.target.value)}
              />
            </div>

            <div className="ad-form-group">
              <label className="ad-form-label">WhatsApp Number</label>
              <input
                type="tel"
                className="ad-form-input"
                placeholder="+968 ..."
                value={bookingForm.whatsapp_number}
                onChange={(e) => updateBookingField('whatsapp_number', e.target.value)}
              />
            </div>

            <div className="ad-form-row">
              <div className="ad-form-group">
                <label className="ad-form-label">Preferred Date</label>
                <input
                  type="date"
                  className="ad-form-input"
                  min={new Date().toISOString().split('T')[0]}
                  value={bookingForm.preferred_date}
                  onChange={(e) => updateBookingField('preferred_date', e.target.value)}
                />
              </div>

              <div className="ad-form-group">
                <label className="ad-form-label">Guests</label>
                <div className="ad-stepper-row">
                  <span className="ad-stepper-label">{bookingForm.guests} {bookingForm.guests > 1 ? 'guests' : 'guest'}</span>
                  <div className="ad-stepper">
                    <button
                      type="button"
                      className="ad-stepper-btn"
                      disabled={bookingForm.guests <= 1}
                      onClick={() => updateBookingField('guests', Math.max(1, bookingForm.guests - 1))}
                    >
                      −
                    </button>
                    <span className="ad-stepper-value">{bookingForm.guests}</span>
                    <button
                      type="button"
                      className="ad-stepper-btn"
                      onClick={() => updateBookingField('guests', bookingForm.guests + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="ad-form-submit-btn"
              disabled={bookingStatus === 'submitting'}
              onClick={handleBookingSubmit}
            >
              {bookingStatus === 'submitting' ? 'Sending…' : 'Confirm Reservation'}
            </button>
          </>
        )}
      </div>
    </div>
  </div>
)}


        <DefaultFooter />
      </div>
    </>
  );
}